import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import QueryKeys from '@src/constants/queryKeys';
import useEventMutations from '@src/hooks/api/mutations/useEventMutations';
import { useQueryClient } from '@tanstack/react-query';
import { UUID } from 'crypto';
import { Dispatch, SetStateAction } from 'react';

interface EventDeleteDialogProps {
  eventId: UUID;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
}

export default function EventDeleteDialog({
  eventId,
  setOpen,
  setAnchorEl,
}: EventDeleteDialogProps) {
  const { deleteCalendarEvent } = useEventMutations(eventId);

  const queryClient = useQueryClient();

  const handleSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.events],
    });
    setAnchorEl(null);
    setOpen(false);
  };

  const isAlreadyDeletedError = (error: Error): boolean => {
    if (!error.cause || typeof error.cause !== 'object') {
      return false;
    }
    const cause = error.cause as Record<string, unknown>;
    const message = cause.message;
    return (
      typeof message === 'string' &&
      message.toLowerCase().includes('already deleted')
    );
  };

  const handleDelete = () =>
    deleteCalendarEvent.mutate(
      {},
      {
        onSuccess: () => handleSuccess(),
        onError: (error) => {
          if (isAlreadyDeletedError(error)) {
            handleSuccess();
            return;
          }
          console.error('Error deleting event:', error);
        },
      },
    );
  return (
    <Dialog open>
      <DialogTitle>Delete Site Event</DialogTitle>
      <DialogContent>
        This will remove Site Event permanently. You cannot undo this action
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setOpen(false)}
          color='inherit'
          disableElevation
          disabled={deleteCalendarEvent.isPending}
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleDelete}
          color='error'
          variant='outlined'
          disableElevation
          loading={deleteCalendarEvent.isPending}
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
